import * as React from 'react';
import moment from 'moment';
import { Alert, AlertIcon, AlertDescription } from '@chakra-ui/react';
import {
  BankLetterList,
  BankStatementDocList,
  BirthCertificateDoc,
  BirthCertificateDocList,
  BirthCertificateDocs,
  FamilySponsorship,
  FamilySponsorshipDoc,
  IDocumentsList,
  ParentLetterDocList
} from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import Finger, { Status } from '../../../atoms/finger';
import { getMaturityDate } from '../../../../utilities/validation';
import {
  bankLetterName,
  bankStatementName,
  bankStatementTransName,
  getDocStatus,
  getVisible
} from '../../../../utilities/documents';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';

interface IProps {
  infoOnly: boolean;
  id: string;
  amount: string;
  financials: FamilySponsorship;
  documents: FamilySponsorshipDoc;
  birthCertificate?: BirthCertificateDocs;
  title?: string;
  warning?: string;
  onResume: (url: string) => void;
  onUploadClick: (
    sponsorType: string,
    docType: string | null,
    docKey: string,
    infoOnly: boolean,
    modalTitle: string
  ) => void;
}

const FamilySection = (props: IProps) => {
  const { financials, documents: familySponsor, infoOnly } = props;
  const famKey = props.id;
  const isFather = financials.accountType === 'Father';
  const isMother = financials.accountType === 'Mother';
  const isGuardian = financials.accountType === 'Guardian';
  const isSpouse = financials.accountType === 'Spouse';
  let hasDocs = false;
  const docNodes: React.ReactNode[] = [];
  const bankStatement = familySponsor.bankStatement;
  const bankTranslation = familySponsor.bankStatementTranslation;
  const bankLetter = familySponsor.bankLetter;
  const parentLetter = familySponsor.parentLetter;
  const birthCertificate = props.birthCertificate;
  const verificationBankLetter = familySponsor.verificationBankLetter;
  const secondaryBankStatement = familySponsor.secondaryBankStatement;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.FINANCE_DOC, offer.status);

  let fundsMaturedError = null;

  const getCertificateStatus = (item: BirthCertificateDoc) => {
    if (isSectionLocked && item.isDocumentFixed) return Status.Locked;
    return item.isDocumentFixed ? Status.Complete : getDocStatus(item.status, infoOnly, isSectionLocked);
  };
  if (birthCertificate && birthCertificate.certificate) {
    const visibleDocs = getVisible(birthCertificate.certificate!, infoOnly) as BirthCertificateDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BirthCertificateDocList, index: number) => {
          const { key, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-birth-certificate-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-${famKey}-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`birth-certificate-${famKey}-${key}`}
                key={`birth-certificate-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getCertificateStatus(item)}
                label={`Birth Certificate ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick(
                    'birthCertificate',
                    'certificate',
                    key,
                    infoOnly,
                    'Birth Certificate or Proof of Guardianship'
                  )
                }
              />
            );
          }
        })
      );
    }
  }

  if (birthCertificate && birthCertificate.translation) {
    const visibleDocs = getVisible(birthCertificate.translation, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-birth-certificate-translation-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-birth-certificate-translation-${famKey}-${key}`}
                status={Status.Rejected}
                label={`Birth Certificate Translation ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`birth-certificate-translation-${famKey}-${key}`}
                key={`birth-certificate-translation-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`Birth Certificate Translation ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('birthCertificate', 'translation', key, infoOnly, 'Birth Certificate Translation')
                }
              />
            );
          }
        })
      );
    }
  }

  if (parentLetter) {
    const visibleDocs = getVisible(parentLetter, infoOnly) as ParentLetterDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const modalTitle = `Letter from your ${isMother ? 'Mother' : isFather ? 'Father' : 'Guardian'}`;
      docNodes.push(
        ...visibleDocs.map((doc: ParentLetterDocList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-letter-from-parent-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-letter-from-parent-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`letter-from-parent-${famKey}-${key}`}
                key={`letter-from-parent-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getCertificateStatus(item)}
                label={`${modalTitle} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onUploadClick('familySponsorship', 'parentLetter', key, infoOnly, `${modalTitle}`)}
              />
            );
          }
        })
      );
    }
  }

  if (bankStatement) {
    const visibleDocs = getVisible(bankStatement, infoOnly) as BankStatementDocList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BankStatementDocList, index: number) => {
          const { key, item } = doc;
          const maturityDate =
            item.belowReqAmount && item.dateBelowReqAmount ? getMaturityDate(item.dateBelowReqAmount) : null;
          fundsMaturedError =
            maturityDate && maturityDate.startOf('day').isAfter(moment().startOf('day'))
              ? maturityDate.format('DD MMM YYYY')
              : null;
          const title = isMother
            ? `Mother's`
            : isFather
            ? `Father's`
            : isGuardian
            ? `Guardian's`
            : isSpouse
            ? `Partner's`
            : `My`;
          const docName = bankStatementName(financials.sponsorType);

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-bank-statement-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-bank-statement-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${title} ${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`bank-statement-${famKey}-${key}`}
                key={`bank-statement-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getCertificateStatus(item)}
                label={`${title} ${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('familySponsorship', 'bankStatement', key, infoOnly, `${title} ${docName}`)
                }
              />
            );
          }
        })
      );
    }
  }

  if (bankLetter) {
    const visibleDocs = getVisible(bankLetter, infoOnly) as BankLetterList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: BankLetterList, index: number) => {
          const { key, item } = doc;
          const docName = bankLetterName(financials.sponsorType);

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-bank-letter-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-bank-letter-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`bank-letter-${famKey}-${key}`}
                key={`bank-letter-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getCertificateStatus(item)}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() => props.onUploadClick('familySponsorship', 'bankLetter', key, infoOnly, `${docName}`)}
              />
            );
          }
        })
      );
    }
  }

  if (bankTranslation) {
    const visibleDocs = getVisible(bankTranslation, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;
          const docName = bankStatementTransName(financials.sponsorType);

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-bank-statement-translation-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-bank-statement-translation-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`bank-statement-translation-${famKey}-${key}`}
                key={`bank-statement-translation-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('familySponsorship', 'bankStatementTranslation', key, infoOnly, `${docName}`)
                }
              />
            );
          }
        })
      );
    }
  }

  if (verificationBankLetter) {
    const visibleDocs = getVisible(verificationBankLetter, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const docName = 'Verification Letter from Bank';
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-verification-bank-letter-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-verification-bank-letter-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`verification-bank-letter-${famKey}-${key}`}
                key={`verification-bank-letter-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('familySponsorship', 'verificationBankLetter', key, infoOnly, docName)
                }
              />
            );
          }
        })
      );
    }
  }

  if (secondaryBankStatement) {
    const visibleDocs = getVisible(secondaryBankStatement, infoOnly) as IDocumentsList[];
    if (visibleDocs.length > 0) {
      hasDocs = true;
      const docName = 'Secondary Bank Statement Scan';
      docNodes.push(
        ...visibleDocs.map((doc: IDocumentsList, index: number) => {
          const { key, item } = doc;

          if (item.status === 'REJECTED') {
            return (
              <Finger
                id={`rejected-secondary-bank-statement-${famKey}-${key}`}
                hideButton={true}
                reason={item.reason}
                key={`rejected-secondary-bank-statement-${famKey}-${key}`}
                status={Status.Rejected}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
              />
            );
          } else {
            return (
              <Finger
                id={`secondary-bank-statement-${famKey}-${key}`}
                key={`secondary-bank-statement-${famKey}-${key}`}
                completeLabel={infoOnly ? 'View' : undefined}
                resolveLabel={infoOnly ? 'Please confirm' : 'Requires attention'}
                buttonLabel={infoOnly ? 'Please review' : 'How to prove'}
                status={getDocStatus(item.status, infoOnly, isSectionLocked)}
                label={`${docName} ${visibleDocs.length > 1 ? (index > 0 ? index + 1 : '') : ''}`}
                onClick={() =>
                  props.onUploadClick('familySponsorship', 'secondaryBankStatement', key, infoOnly, docName)
                }
              />
            );
          }
        })
      );
    }
  }

  if (!hasDocs) return null;

  return (
    <ChecklistSection
      key={`checklist-section-${famKey}`}
      label={`Myself or my family`}
      edit={() => {
        props.onResume('finances/step-1');
      }}
      warning={props.warning}
      amount={props.amount}
      id={props.id}
    >
      {fundsMaturedError ? (
        <Alert status="warning" my={2}>
          <AlertIcon />
          <AlertDescription>Finances mature after {fundsMaturedError}</AlertDescription>
        </Alert>
      ) : null}
      {docNodes}
    </ChecklistSection>
  );
};

export default FamilySection;
